<template>
	<div class="Rating" :class="reverse ? 'Rating--reverse' : ''">
		<button type="button" @click="editRating" ref="ratingButton">
			<template v-if="currentRating">
				<CloRatingStars :rating="currentRating" :ratingCount="currentCount" label="Ratings" :reverse="reverse" class="u-stack-xs" />
				<div v-if="currentUserRating" class="Rating-user" :class="saving || saved ? 'Rating-user--saved' : ''">
					<CloIcon v-if="!saving" symbol="check" />
					<CloLoadingAnimation v-if="saving && !error" class="LoadingAnimation--small" />
					<span v-if="!saving && !error">
						{{ $t('mediaRating.yourRating') }}
						<span class="Rating-user-value">{{ currentUserRating }}</span>
					</span>
				</div>
				<div v-else class="Rating-user">
					<CloIcon symbol="plus" />
					<span>{{ $t('mediaRating.rateQuality') }}</span>
				</div>
			</template>
			<div v-else class="Rating-user u-stack-md">
				<CloIcon symbol="star" />
				<span>{{ $t('mediaRating.noRating') }}</span>
			</div>
			<div v-if="error" class="Rating-error">{{ $t('mediaRating.error') }}</div>
		</button>
		<transition name="fade">
			<div
				v-if="edit"
				class="Rating-edit"
				id="rating-edit"
				ref="ratingEdit"
				tabindex="-1"
				:style="editStyle"
				@keyup.escape.stop.prevent="closeEdit"
			>
				<fieldset>
					<legend>{{ legend }}</legend>
					<div class="Rating-edit-stars">
						<span v-for="star in stars" :key="star" class="Rating-edit-star" :class="starClasses[star - 1]">
							<input
								type="radio"
								name="rating"
								:value="star"
								:id="'rating-' + star"
								v-model="currentUserRating"
								@change="saveRating"
							/>
							<label :for="'rating-' + star" @mouseenter="mouseOver = star" @mouseleave="mouseOver = null">
								<span class="is-visuallyHidden">{{ star }}</span>
								<CloIcon symbol="star" />
							</label>
						</span>
					</div>
					<div class="Rating-help">
						<a :href="helpUrl">
							<CloIcon symbol="help" />
							{{ $t('mediaRating.ratingHelp') }}
						</a>
						<span>{{ $t('mediaRating.ratingKeyHelp') }}</span>
					</div>
				</fieldset>
				<button class="Rating-edit-close" type="button" @click="edit = false">
					<span class="is-visuallyHidden">{{ $t('generic.Close') }}</span>
					<CloIcon symbol="close" />
				</button>
			</div>
		</transition>
	</div>
</template>

<script>
import axios from 'axios';
import CloIcon from '@/icons/CloIcon/CloIcon.vue';
import CloLoadingAnimation from '@/base/CloLoadingAnimation/CloLoadingAnimation.vue';
import CloRatingStars from '@/media/CloRatingStars/CloRatingStars.vue';
export default {
	components: { CloIcon, CloRatingStars, CloLoadingAnimation },
	data() {
		return {
			edit: false,
			stars: [1, 2, 3, 4, 5],
			saving: false,
			saved: false,
			error: false,
			currentUserRating: null,
			currentCount: 0,
			mouseOver: null,
			editStyle: {
				left: 0,
				right: 'auto',
				top: 0,
				bottom: 'auto',
			},
		};
	},
	props: {
		/**
		 * Number of ratings to give
		 */
		rating: {
			type: Number,
			default: null,
		},
		count: {
			type: Number,
			default: 0,
		},
		userRating: {
			type: Number,
			default: null,
		},
		/**
		 * To reverse the component or not
		 */
		reverse: {
			type: Boolean,
			default: false,
		},
		mediaType: {
			type: String,
			default: '',
		},
		assetId: {
			type: String,
			required: true,
		},
		getRatingUrl: {
			type: String,
			default: null,
			//default: '/rating-api?assetIds=',
		},
		saveRatingUrl: {
			type: String,
			default: '/rating-api',
		},
		ratingApiHeader: {
			type: String,
			default: null,
		},
		ratingCsrf: {
			type: String,
			default: null,
		},
		keyboardListener: {
			type: Boolean,
			default: false,
		},
		ratingFromParent: {
			type: Number,
			default: 0,
		},
	},
	created() {
		this.currentRating = this.rating;
		this.currentCount = this.count;
		this.currentUserRating = this.userRating;
		if (this.getRatingUrl) {
			this.getRating();
		}
	},
	mounted() {
		if (this.keyboardListener) {
			document.addEventListener('keyup', this.handleKeyboard);
		}
	},
	beforeDestroy() {
		if (this.keyboardListener) {
			document.removeEventListener('keyup', this.handleKeyboard);
		}
	},
	watch: {
		ratingFromParent: function (newRating) {
			this.currentUserRating = newRating;
			this.saveRating();
		},
		userRating: function (newRating) {
			this.currentUserRating = newRating;
			this.$forceUpdate();
		},
	},
	computed: {
		starClasses() {
			return this.stars.map((item, index) => {
				const classes = [];
				if ((!this.mouseOver && item <= this.currentUserRating) || item <= this.mouseOver) {
					classes.push('Rating-edit-star--filled');
				}
				return classes.join(' ');
			});
		},
		legend() {
			switch (this.mediaType) {
				case 'p':
					return this.$t('mediaRating.legendImage');
					break;
				case 'a':
					return this.$t('mediaRating.legendAudio');
					break;
				case 'v':
					return this.$t('mediaRating.legendVideo');
					break;
				default:
					return this.$t('mediaRating.rateQuality');
			}
		},
		helpUrl() {
			const prefix = 'https://support.ebird.org/en/support/solutions/articles/48001064392-rating-media';
			switch (this.mediaType) {
				case 'p':
					return prefix + '#RatingPhotos';
					break;
				case 'a':
					return prefix + '#RatingAudio';
					break;
				case 'v':
					return prefix + '#RatingVideos';
					break;
				default:
					return prefix;
			}
		},
	},
	methods: {
		saveRating() {
			if (this.currentUserRating > 0 && this.currentUserRating <= 5) {
				this.edit = false;
				this.saving = true;
				this.error = false;
				const url = this.saveRatingUrl + this.assetId + '/my-rating';
				const headers = {
					'X-Requested-With': 'XMLHttpRequest',
				};
				if (this.ratingApiHeader) {
					headers['Session-Inheritance-Config'] = this.ratingApiHeader;
				}
				if (this.ratingCsrf) {
					headers['X-CSRF-TOKEN'] = this.ratingCsrf;
				}
				let bodyFormData = new FormData();
				bodyFormData.append('rating', this.currentUserRating);
				axios({
					method: 'post',
					url: url,
					data: bodyFormData,
					headers: headers,
				})
					.then((res) => {
						if (res.data.assetId == this.assetId) {
							this.currentRating = Math.round(res.data.ratingAverage);
							this.currentCount = res.data.ratingCount;
							this.saving = false;
							this.error = false;
							this.saved = true;
							this.$emit('rating-update', res.data);
							this.$forceUpdate();
						}
					})
					.catch((err) => {
						this.error = true;
					});
			}
		},
		getRating() {
			const url = this.getRatingUrl + this.assetId;
			const headers = {
				'X-Requested-With': 'XMLHttpRequest',
			};
			if (this.ratingApiHeader) {
				headers['Session-Inheritance-Config'] = this.ratingApiHeader;
			}
			if (this.ratingCsrf) {
				headers['X-CSRF-TOKEN'] = this.ratingCsrf;
			}
			axios
				.get(url, null, headers)
				.then((res) => {
					if (res.data.hasOwnProperty(this.assetId)) {
						this.currentRating = Math.round(res.data[this.assetId].ratingAverage);
						this.currentCount = res.data[this.assetId].ratingCount;
						this.currentUserRating = res.data[this.assetId].rating;
						this.$forceUpdate();
					}
				})
				.catch((err) => {
					console.log(err);
					this.currentRating = this.rating;
					this.currentCount = this.count;
					this.currentUserRating = this.userRating;
				});
		},
		editRating() {
			const viewportOffset = this.$el.getBoundingClientRect();
			const style = {
				left: 0,
				right: 'auto',
				top: 0,
				bottom: 'auto',
			};
			if (window.innerHeight - viewportOffset.bottom < viewportOffset.top) {
				style.bottom = 0;
				style.top = 'auto';
			}
			if (viewportOffset.left > window.innerWidth - viewportOffset.right) {
				style.right = 0;
				style.left = 'auto';
			}
			this.editStyle = style;
			this.edit = true;
			this.$nextTick(() => {
				this.$refs.ratingEdit.focus();
			});
		},
		closeEdit() {
			this.edit = false;
			this.$refs.ratingButton.focus();
		},
		handleKeyboard(e) {
			if ((e.target.tagName === 'INPUT' && e.target.type !== 'radio') || e.target.tagName === 'TEXTAREA') {
				return; // don't interfere with typing
			}
			if (!e.shiftKey && !e.metaKey && !e.ctrlKey && !e.altKey) {
				switch (e.which) {
					case 49: // 1
					case 97:
						this.handleRatingByKey(1, e);
						break;
					case 50: // 2
					case 98:
						this.handleRatingByKey(2, e);
						break;
					case 51: // 3
					case 99:
						this.handleRatingByKey(3, e);
						break;
					case 52: // 4
					case 100:
						this.handleRatingByKey(4, e);
						break;
					case 53: // 5
					case 101:
						this.handleRatingByKey(5, e);
						break;
				}
			}
		},
		handleRatingByKey(rating, e) {
			e.preventDefault();
			e.stopPropagation();
			this.currentUserRating = rating;
			this.saveRating();
		},
	},
};
</script>

<style lang="scss">
.Rating {
	--star-color: #{$brand-color-forest-green-9};

	display: inline-flex;
	position: relative;
	@include text(3);
	.Rating-user {
		display: flex;
		align-items: center;
		color: $color-link;
		text-align: left;
		[dir='rtl'] & {
			text-align: right;
		}
		.Icon {
			@include icon-sm;
			@include inline-xs;
		}
		.Rating-user-value {
			font-weight: bold;
		}
		.Icon--spinner {
			// avoid layout shifts
			width: 16px;
			height: 15px;
		}
		&--saved {
			.Icon--check {
				fill: var(--star-color);
			}
			.Rating-user-value {
				color: var(--star-color);
			}
		}
	}
	.Rating-edit {
		position: absolute;
		background-color: $color-white;
		border: 1px solid $color-neutral-2;
		min-width: 240px;
		z-index: 1007;
		@include inset-sm;
		@include dropshadow-2;
		&.fade-enter-active,
		&.fade-leave-active {
			transition: opacity 0.2s;
		}
		&.fade-enter,
		&.fade-leave-to {
			opacity: 0;
		}
		fieldset {
			position: relative;
			border: 0;
			padding: 0;
			margin: 0;
		}
		.Rating-edit-stars {
			@include stack-xs;
			display: flex;
			justify-content: stretch;
			.Icon {
				@include icon-lg;
				fill: transparent;
				stroke: $color-link;
				stroke-width: 1;
				transition: fill 0.2s ease, stroke 0.2s ease;
			}
			.Rating-edit-star {
				position: relative;
				display: flex;
				flex-grow: 1;
				&:focus-within {
					outline: 2px solid blue;
				}
			}
			.Rating-edit-star--filled .Icon {
				fill: var(--star-color);
				stroke: var(--star-color);
			}
			[type='radio'] {
				opacity: 0;
				position: absolute;
			}
			label {
				display: flex;
				flex-grow: 1;
				@include inset-sm;
				margin: 0;
				cursor: pointer;
			}
		}
		.Rating-help {
			@include text(2);
			display: flex;
			a {
				@include inline-sm;
			}
			.Icon {
				@include icon-sm;
			}
			span {
				justify-self: flex-end;
			}
		}
		.Rating-edit-close {
			position: absolute;
			right: $space-sm;
			top: $space-sm;
			[dir='rtl'] & {
				right: auto;
				left: $space-sm;
			}
			display: flex;
			.Icon {
				@include icon-sm;
				fill: $color-neutral-3;
			}
		}
	}
	.Rating-error {
		max-width: 320px;
		text-align: left;
		[dir='rtl'] & {
			text-align: right;
		}
	}
}

.Rating--reverse {
	--star-color: #{$brand-color-forest-green-dark-9};
	color: $color-text-reverse;
	.Rating-user {
		color: $color-link-reverse;
		&--saved {
			.Icon--check {
				fill: var(--star-color);
			}
			.Rating-user-value {
				color: var(--star-color);
			}
		}
	}
	.Rating-edit {
		background-color: $brand-color-sand-dark-2;
		border: 1px solid $brand-color-sand-dark-5;
		.Rating-edit-stars {
			.Icon {
				stroke: var(--star-color);
			}
			.Rating-edit-star--filled .Icon {
				fill: var(--star-color);
				stroke: var(--star-color);
			}
		}
		.Rating-edit-close {
			.Icon {
				fill: $color-text-muted-reverse;
			}
		}
	}
	a {
		color: $color-link-reverse;
	}
}
</style>
